import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import {
	fetchNotificationCenterIndex,
	fetchNotificationCenterItem,
} from "actions/notificationCenterActions";
import type { RootState } from "store";
import type { NotificationCenterData } from "types";

const initialState: NotificationCenterData = {
	notifications: [],
	isLoading: true,
	isFetching: false,
	currentPage: 1,
	totalItems: 0,
	itemsPerPage: 10,
	selectedFilter: "all",
};

const notificationCenterSlice = createSlice({
	name: "notificationCenter",
	initialState,
	reducers: {
		setCurrentPage: (state, action: PayloadAction<number>) => {
			state.currentPage = action.payload;
		},
		setSelectedFilter: (state, action: PayloadAction<string>) => {
			state.selectedFilter = action.payload;
			state.currentPage = 1; // Reset to first page when filter changes
		},
		setItemsPerPage: (state, action: PayloadAction<number>) => {
			state.itemsPerPage = action.payload;
			state.currentPage = 1; // Reset to first page when items per page changes
		},
		updateNotificationContent: (
			state,
			action: PayloadAction<{ fileName: string; content: string }>,
		) => {
			const notification = state.notifications.find(
				(n) => n.file === action.payload.fileName,
			);
			if (notification) {
				notification.content = action.payload.content;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchNotificationCenterIndex.pending, (state) => {
				state.isFetching = true;
			})
			.addCase(fetchNotificationCenterIndex.fulfilled, (state, { payload }) => {
				if (payload) {
					// Handle case where payload might be a JSON string instead of parsed object
					let notifications = payload;
					if (typeof payload === "string") {
						try {
							notifications = JSON.parse(payload);
						} catch (error) {
							console.error("Failed to parse notification index:", error);
							notifications = [];
						}
					}

					state.notifications = Array.isArray(notifications) ? notifications : [];
					state.totalItems = state.notifications.length;
				}
				state.isFetching = false;
				state.isLoading = false;
			})
			.addCase(fetchNotificationCenterIndex.rejected, (state) => {
				state.isFetching = false;
				state.isLoading = false;
			})
			.addCase(fetchNotificationCenterItem.fulfilled, (state, { payload, meta }) => {
				const fileName = meta.arg.fileName;
				if (payload) {
					// Handle escaped content from Azure Data Lake
					let content = payload;
					if (
						typeof content === "string" &&
						content.startsWith('"') &&
						content.endsWith('"')
					) {
						try {
							// Remove outer quotes and parse escape sequences
							content = JSON.parse(content);
						} catch (error) {
							// If parsing fails, manually unescape common sequences
							content = content
								.slice(1, -1)
								.replace(/\\"/g, '"')
								.replace(/\\n/g, "\n")
								.replace(/\\r/g, "\r")
								.replace(/\\t/g, "\t")
								.replace(/\\\\/g, "\\");
						}
					}

					const notification = state.notifications.find((n) => n.file === fileName);
					if (notification) {
						notification.content = content;
					}
				}
			});
	},
});

export const { setCurrentPage, setSelectedFilter, setItemsPerPage, updateNotificationContent } =
	notificationCenterSlice.actions;

export default notificationCenterSlice.reducer;

// Selectors
export const selectNotificationCenter = (state: RootState) => state.notificationCenter;

export const selectFilteredNotifications = (state: RootState) => {
	const { notifications, selectedFilter } = state.notificationCenter;

	if (selectedFilter === "all") {
		return notifications;
	}

	return notifications.filter((notification) => {
		const productFilter = selectedFilter.toLowerCase();
		return notification.products.some(
			(product) =>
				product.toLowerCase() === productFilter ||
				(productFilter === "yits" && product.toLowerCase().includes("yits")) ||
				(productFilter === "itye" && product.toLowerCase().includes("itye")) ||
				(productFilter === "common" && product.toLowerCase() === "common"),
		);
	});
};

export const selectPaginatedNotifications = (state: RootState) => {
	const filteredNotifications = selectFilteredNotifications(state);
	const { currentPage, itemsPerPage } = state.notificationCenter;

	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;

	return filteredNotifications.slice(startIndex, endIndex);
};

export const selectNotificationCenterStats = (state: RootState) => {
	const filteredNotifications = selectFilteredNotifications(state);
	const { currentPage, itemsPerPage } = state.notificationCenter;

	return {
		totalFiltered: filteredNotifications.length,
		totalPages: Math.ceil(filteredNotifications.length / itemsPerPage),
		currentPage,
		itemsPerPage,
		startIndex: (currentPage - 1) * itemsPerPage + 1,
		endIndex: Math.min(currentPage * itemsPerPage, filteredNotifications.length),
	};
};
